import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DemoFarmInputs, type TestPacketsInputs, type AreaUnit, type WeightUnit, AREA_FACTORS, WEIGHT_FACTORS, AREA_LABELS, WEIGHT_LABELS } from './types';
import { formatNumber } from './utils';

interface DemoFarmBreakdownProps {
  type: 'demoFarm';
  cropLabel: string;
  cropId: string;
  inputs: DemoFarmInputs;
  currencyCode: string;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
  result: {
    seedKg: number;
    hectares: number;
    costLocal: number;
    costUsd: number;
  };
}

interface TestPacketsBreakdownProps {
  type: 'testPackets';
  cropLabel: string;
  cropId: string;
  inputs: TestPacketsInputs;
  totalPackets: number;
  seedCostPerKg: number;
  seedRatePerHa: number;
  currencyCode: string;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
  result: {
    seedKg: number;
    equivalentHa: number;
    costLocal: number;
    costUsd: number;
  };
}

type Props = DemoFarmBreakdownProps | TestPacketsBreakdownProps;

export function CalculationBreakdown(props: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs text-spi-green hover:text-spi-green-dark flex items-center gap-1 transition-colors"
        title="Show calculation breakdown"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {t('breakdown.how')}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-6 z-50 w-80 bg-white rounded-xl shadow-2xl border border-spi-parchment p-4 text-left">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-spi-green-dark text-sm">
                {t('breakdown.calculation', { crop: props.cropLabel })}
              </h4>
              <button
                onClick={() => setIsOpen(false)}
                className="text-spi-slate hover:text-spi-charcoal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {props.type === 'demoFarm' ? (
              <DemoFarmBreakdown {...props} />
            ) : (
              <TestPacketsBreakdown {...props} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DemoFarmBreakdown({ cropLabel, cropId, inputs, currencyCode, areaUnit, weightUnit, result }: DemoFarmBreakdownProps) {
  const { t } = useTranslation();
  const af = AREA_FACTORS[areaUnit];
  const wf = WEIGHT_FACTORS[weightUnit];
  const aL = AREA_LABELS[areaUnit];
  const wL = WEIGHT_LABELS[weightUnit];
  const percentage = inputs.cropPercentages[cropId] ?? 0;
  const seedRate = inputs.seedRatesKgPerHa[cropId] ?? 0;
  const costPerKg = inputs.seedCostsLocalPerKg[cropId] ?? 0;

  return (
    <div className="space-y-3 text-xs">
      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.demoStep1', { crop: cropLabel })}</p>
        <code className="block text-spi-slate">
          {formatNumber(inputs.farmHectares * af)} {aL} × ({percentage}% ÷ 100)
        </code>
        <p className="text-spi-green font-medium mt-1">= {formatNumber(result.hectares * af)} {aL}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.demoStep2')}</p>
        <code className="block text-spi-slate">
          {formatNumber(result.hectares * af)} {aL} × {formatNumber(seedRate * wf / af)} {wL}/{aL}
        </code>
        <p className="text-spi-green font-medium mt-1">= {formatNumber(result.seedKg * wf)} {wL}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.demoStep3', { currency: currencyCode })}</p>
        <code className="block text-spi-slate">
          {formatNumber(result.seedKg * wf)} {wL} × {formatNumber(costPerKg / wf)} {currencyCode}/{wL}
        </code>
        <p className="text-spi-green font-medium mt-1">= {result.costLocal.toLocaleString()} {currencyCode}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.demoStep4')}</p>
        <code className="block text-spi-slate">
          {result.costLocal.toLocaleString()} {currencyCode} ÷ rate
        </code>
        <p className="text-spi-orange font-medium mt-1">= ${formatNumber(result.costUsd)} USD</p>
      </div>
    </div>
  );
}

function TestPacketsBreakdown({ cropLabel, cropId, inputs, totalPackets, seedCostPerKg, seedRatePerHa, currencyCode, areaUnit, weightUnit, result }: TestPacketsBreakdownProps) {
  const { t } = useTranslation();
  const af = AREA_FACTORS[areaUnit];
  const wf = WEIGHT_FACTORS[weightUnit];
  const aL = AREA_LABELS[areaUnit];
  const wL = WEIGHT_LABELS[weightUnit];
  const packetWeight = inputs.packetWeightsKg[cropId] ?? 0;

  return (
    <div className="space-y-3 text-xs">
      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.testTotalPackets')}</p>
        <code className="block text-spi-slate">
          ({inputs.refugeeHouseholds} × {inputs.avgPacketsPerRefugeeHousehold}) + ({inputs.hostCommunityHouseholds} × {inputs.avgPacketsPerHostHousehold})
        </code>
        <p className="text-spi-green font-medium mt-1">= {formatNumber(totalPackets)} packets</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.testStep1', { crop: cropLabel })}</p>
        <code className="block text-spi-slate">
          {formatNumber(totalPackets)} packets × {formatNumber(packetWeight * wf)} {wL}
        </code>
        <p className="text-spi-green font-medium mt-1">= {formatNumber(result.seedKg * wf)} {wL}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.testStep2')}</p>
        <code className="block text-spi-slate">
          {formatNumber(result.seedKg * wf)} {wL} ÷ {formatNumber(seedRatePerHa * wf / af)} {wL}/{aL}
        </code>
        <p className="text-spi-green font-medium mt-1">= {formatNumber(result.equivalentHa * af)} {aL}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.testStep3', { currency: currencyCode })}</p>
        <code className="block text-spi-slate">
          {formatNumber(result.seedKg * wf)} {wL} × {formatNumber(seedCostPerKg / wf)} {currencyCode}/{wL}
        </code>
        <p className="text-spi-green font-medium mt-1">= {result.costLocal.toLocaleString()} {currencyCode}</p>
      </div>

      <div className="bg-spi-seed p-3 rounded-lg">
        <p className="font-medium text-spi-charcoal mb-1">{t('breakdown.testStep4')}</p>
        <code className="block text-spi-slate">
          {result.costLocal.toLocaleString()} {currencyCode} ÷ rate
        </code>
        <p className="text-spi-orange font-medium mt-1">= ${formatNumber(result.costUsd)} USD</p>
      </div>
    </div>
  );
}
